import { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Check, Bot, Cpu, MessageSquare, Plug, Lightbulb, Plus, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { SectionHeader } from '../ui/section-header';
import { cn } from '@/lib/utils';
import { useConnectors } from '../../lib/use-connectors';
import { useSkills } from '../../lib/use-skills';
import { useProviders, useProviderTemplates } from '../../lib/use-providers';
import * as agentApi from '../../lib/agent-api';
import type { Agent, CreateAgentRequest } from '../../lib/agent-types';
import type { Provider, ProviderModel } from '../../lib/provider-types';

interface AgentDialogProps {
  agent?: Agent | null;
  workspaceId: string | null;
  onClose: () => void;
  onSave: (data: CreateAgentRequest) => Promise<Agent>;
  variant?: 'modal' | 'panel';
}

type Step = 'basic' | 'prompt' | 'connectors' | 'skills' | 'model';

const STEP_META: Record<Step, { label: string; icon: typeof Bot }> = {
  basic: { label: 'Config', icon: Bot },
  prompt: { label: 'Prompt', icon: MessageSquare },
  connectors: { label: 'Connect', icon: Plug },
  skills: { label: 'Skills', icon: Lightbulb },
  model: { label: 'Provider', icon: Cpu },
};

export function AgentDialog({ agent, workspaceId, onClose, onSave, variant = 'modal' }: AgentDialogProps) {
  const [step, setStep] = useState<Step>('basic');
  const [formData, setFormData] = useState({
    name: agent?.name || '',
    description: agent?.description || '',
    systemPrompt: agent?.config?.systemPrompt || '',
    providerId: agent?.providerId?.toString() || '',
    providerModelId: agent?.providerModelId?.toString() || '',
    temperature: agent?.config?.temperature?.toString() || '0.7',
    maxTokens: agent?.config?.maxTokens?.toString() || '4096',
    quickStartPrompts: agent?.quickStartPrompts
      ? Object.entries(agent.quickStartPrompts).map(([k, v]) => ({ key: k, value: v }))
      : [] as Array<{ key: string; value: string }>,
  });
  const [selectedConnectors, setSelectedConnectors] = useState<Set<string>>(new Set());
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data from backend
  const { providers, loading: providersLoading } = useProviders(workspaceId);
  const { connectors } = useConnectors();
  const { skills: allSkills } = useSkills(workspaceId);

  // Derive model list from selected provider
  const selectedProvider = providers.find((p) => p.id.toString() === formData.providerId);
  const availableModels: ProviderModel[] = selectedProvider?.providerModels
    ?? selectedProvider?.providerTemplate?.models
    ?? [];

  // Auto-select first provider if none selected
  useEffect(() => {
    if (!formData.providerId && providers.length > 0) {
      const defaultProvider =
        providers.find((p) => p.isPlatformManaged) ?? providers[0];
      const defaultModel =
        defaultProvider.providerModels?.find((m) => m.isDefault)
        ?? defaultProvider.providerModels?.[0]
        ?? defaultProvider.providerTemplate?.models?.[0];

      setFormData((prev) => ({
        ...prev,
        providerId: defaultProvider.id.toString(),
        providerModelId: defaultModel?.id?.toString() || '',
      }));
    }
  }, [providers, formData.providerId]);

  // Load existing connector/skill selections when editing
  useEffect(() => {
    if (!agent?.id || !workspaceId) return;
    agentApi.fetchAgent(workspaceId, agent.id).then((fullAgent) => {
      if (fullAgent.connectors?.length) {
        setSelectedConnectors(
          new Set(
            fullAgent.connectors.map((c: any) =>
              String(c.connectorId ?? c.connector?.id),
            ),
          ),
        );
      }
      if (fullAgent.skills?.length) {
        setSelectedSkills(new Set(fullAgent.skills.map((s: any) => s.skillId || s.skill?.id)));
      }
    }).catch((err) => {
      console.error('Failed to load agent relations:', err);
    });
  }, [agent?.id, workspaceId]);

  const steps: Step[] = ['basic', 'prompt', 'connectors', 'skills', 'model'];
  const currentStepIndex = steps.indexOf(step);

  // Validation functions for each step
  const validateStep = (stepToValidate: Step): { isValid: boolean; error: string } => {
    switch (stepToValidate) {
      case 'basic':
        if (!formData.name.trim()) {
          return { isValid: false, error: 'Agent name is required' };
        }
        if (formData.name.trim().length < 2) {
          return { isValid: false, error: 'Agent name must be at least 2 characters' };
        }
        return { isValid: true, error: '' };

      case 'prompt':
        if (!formData.systemPrompt.trim()) {
          return { isValid: false, error: 'System prompt is required' };
        }
        if (formData.systemPrompt.trim().length < 10) {
          return { isValid: false, error: 'System prompt must be at least 10 characters' };
        }
        return { isValid: true, error: '' };

      case 'connectors':
        // Connectors are optional, always valid
        return { isValid: true, error: '' };

      case 'skills':
        // Skills are optional, always valid
        return { isValid: true, error: '' };

      case 'model':
        if (!formData.providerId) {
          return { isValid: false, error: 'Please select a provider' };
        }
        if (!formData.providerModelId) {
          return { isValid: false, error: 'Please select a model' };
        }
        return { isValid: true, error: '' };

      default:
        return { isValid: true, error: '' };
    }
  };

  // Check if current step is valid for Next button
  const currentStepValidation = validateStep(step);
  const canProceed = currentStepValidation.isValid;

  const handleNext = () => {
    // Validate current step before proceeding
    const validation = validateStep(step);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }
    
    setError(null);
    if (currentStepIndex < steps.length - 1) setStep(steps[currentStepIndex + 1]);
  };

  const handleBack = () => {
    setError(null);
    if (currentStepIndex > 0) setStep(steps[currentStepIndex - 1]);
  };

  // Handle step navigation from indicator - only allow going to completed steps
  const handleStepClick = (targetStep: Step) => {
    const targetIndex = steps.indexOf(targetStep);
    
    // Allow going back to any previous step
    if (targetIndex < currentStepIndex) {
      setError(null);
      setStep(targetStep);
      return;
    }
    
    // Allow clicking current step
    if (targetIndex === currentStepIndex) {
      return;
    }
    
    // Validate all steps between current and target
    for (let i = currentStepIndex; i < targetIndex; i++) {
      const validation = validateStep(steps[i]);
      if (!validation.isValid) {
        setError(`Please complete "${STEP_META[steps[i]].label}" step first: ${validation.error}`);
        return;
      }
    }
    
    // All intermediate steps are valid, allow navigation
    setError(null);
    setStep(targetStep);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Agent name is required');
      setStep('basic');
      return;
    }

    if (!formData.providerId || !formData.providerModelId) {
      setError('Please select a provider and model');
      setStep('model');
      return;
    }

    if (!formData.systemPrompt || formData.systemPrompt.trim().length < 10) {
      setError('System prompt is required (minimum 10 characters)');
      setStep('prompt');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Build quickStartPrompts as Record<string, string>
      const qsp: Record<string, string> = {};
      formData.quickStartPrompts.forEach((p) => {
        const k = p.key.trim();
        const v = p.value.trim();
        if (k && v) qsp[k] = v;
      });

      const data: CreateAgentRequest = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        config: {
          systemPrompt: formData.systemPrompt.trim(),
          temperature: parseFloat(formData.temperature) || 0.7,
          maxTokens: parseInt(formData.maxTokens) || 4096,
        },
        providerId: parseInt(formData.providerId, 10),
        providerModelId: parseInt(formData.providerModelId, 10),
        ...(Object.keys(qsp).length > 0 && { quickStartPrompts: qsp }),
      };

      // Include connectors and skills in the create/update payload
      if (selectedConnectors.size > 0) {
        data.connectors = Array.from(selectedConnectors).map((id) => ({
          connectorId: parseInt(id, 10),
          isActive: true,
        }));
      } else if (agent?.id) {
        data.connectors = [];
      }
      if (selectedSkills.size > 0) {
        data.skills = Array.from(selectedSkills).map((id) => ({
          skillId: id,
          isActive: true,
        }));
      } else if (agent?.id) {
        data.skills = [];
      }

      await onSave(data);
      onClose();
    } catch (err) {
      console.error('Failed to save agent:', err);
      setError(err instanceof Error ? err.message : 'Failed to save agent');
    } finally {
      setSaving(false);
    }
  };

  const toggleConnector = (connectorId: string) => {
    setSelectedConnectors((prev) => {
      const next = new Set(prev);
      if (next.has(connectorId)) next.delete(connectorId);
      else next.add(connectorId);
      return next;
    });
  };

  const toggleSkill = (skillId: string) => {
    setSelectedSkills((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) next.delete(skillId);
      else next.add(skillId);
      return next;
    });
  };

  const addQuickStart = () => {
    if (formData.quickStartPrompts.length >= 4) return;
    setFormData({
      ...formData,
      quickStartPrompts: [...formData.quickStartPrompts, { key: '', value: '' }],
    });
  };

  const updateQuickStart = (index: number, field: 'key' | 'value', val: string) => {
    const updated = [...formData.quickStartPrompts];
    updated[index] = { ...updated[index], [field]: val };
    setFormData({ ...formData, quickStartPrompts: updated });
  };

  const removeQuickStart = (index: number) => {
    setFormData({
      ...formData,
      quickStartPrompts: formData.quickStartPrompts.filter((_, i) => i !== index),
    });
  };

  const stepIndicator = (
    <div className="w-full flex items-center gap-2">
      {steps.map((s, idx) => {
        const isComplete = idx < currentStepIndex;
        const isCurrent = idx === currentStepIndex;
        const isAccessible = idx <= currentStepIndex || isComplete;

        return (
          <button
            key={s}
            onClick={() => handleStepClick(s)}
            disabled={!isAccessible && idx > currentStepIndex}
            className={cn(
              'flex-1 h-1.5 rounded-full transition-colors',
              isCurrent && 'bg-primary',
              isComplete && !isCurrent && 'bg-primary/60',
              !isComplete && !isCurrent && idx > currentStepIndex && 'bg-muted/60 cursor-not-allowed',
              !isComplete && !isCurrent && idx <= currentStepIndex && 'bg-muted hover:bg-accent'
            )}
            title={STEP_META[s].label}
            aria-label={STEP_META[s].label}
          />
        );
      })}
    </div>
  );

  const content = (
    <>
      {/* ─── Content ───────────────────────── */}
      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {error && (
          <div className="flex items-start gap-2 bg-destructive/10 text-destructive text-xs p-3 rounded-lg border border-destructive/20 mb-4">
            <AlertCircle className="size-3.5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Step 1: Basic Info */}
        {step === 'basic' && (
          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-sm font-medium">
                Agent Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Sales Assistant"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-sm font-medium">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Quick Start Prompts</Label>
              <p className="text-[11px] text-muted-foreground">Add up to 4 sample prompts for users</p>
              <div className="space-y-2">
                {formData.quickStartPrompts.map((prompt, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={prompt.key}
                      onChange={(e) => updateQuickStart(idx, 'key', e.target.value)}
                      placeholder="Label"
                      className="w-1/3"
                    />
                    <Input
                      value={prompt.value}
                      onChange={(e) => updateQuickStart(idx, 'value', e.target.value)}
                      placeholder="Prompt"
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeQuickStart(idx)}
                      className="size-8"
                    >
                      <X className="size-3" />
                    </Button>
                  </div>
                ))}
                {formData.quickStartPrompts.length < 4 && (
                  <Button variant="outline" size="sm" onClick={addQuickStart} className="h-8 text-xs">
                    <Plus className="size-3.5 mr-1" />
                    Add Quick Start
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Prompt */}
        {step === 'prompt' && (
          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="systemPrompt" className="text-sm font-medium">
                System Prompt <span className="text-destructive">*</span>
              </Label>
              <textarea
                id="systemPrompt"
                value={formData.systemPrompt}
                onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
                className="w-full min-h-[140px] rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Define how the agent should behave..."
              />
              <p className="text-[11px] text-muted-foreground">Minimum 10 characters</p>
            </div>
          </div>
        )}

        {/* Step 3: Connectors */}
        {step === 'connectors' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">External Connectors</p>
                <p className="text-[11px] text-muted-foreground">Select which connectors this agent can access</p>
              </div>
              <Badge variant="secondary" className="text-[10px]">
                {selectedConnectors.size} of {connectors.length} selected
              </Badge>
            </div>
            <div className="grid gap-2">
              {connectors.map((connector) => {
                const connectorId = connector.id.toString();
                const selected = selectedConnectors.has(connectorId);
                return (
                  <button
                    key={connectorId}
                    onClick={() => toggleConnector(connectorId)}
                    className={cn(
                      'flex items-center justify-between gap-2 rounded-lg border p-3 text-left transition-colors',
                      selected ? 'border-primary bg-primary/5' : 'hover:bg-accent'
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{connector.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{connector.description || 'No description'}</p>
                    </div>
                    {selected && <Check className="size-4 text-primary" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 4: Skills */}
        {step === 'skills' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Agent Skills</p>
                <p className="text-[11px] text-muted-foreground">Select skills this agent can use</p>
              </div>
              <Badge variant="secondary" className="text-[10px]">
                {selectedSkills.size} of {allSkills.length} selected
              </Badge>
            </div>
            <div className="grid gap-2">
              {allSkills.map((skill) => {
                const skillId = skill.id;
                const selected = selectedSkills.has(skillId);
                return (
                  <button
                    key={skillId}
                    onClick={() => toggleSkill(skillId)}
                    className={cn(
                      'flex items-center justify-between gap-2 rounded-lg border p-3 text-left transition-colors',
                      selected ? 'border-primary bg-primary/5' : 'hover:bg-accent'
                    )}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{skill.skillName}</p>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{skill.skillType}</Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground break-words line-clamp-2">{skill.description || 'No description'}</p>
                    </div>
                    {selected && <Check className="size-4 text-primary" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 5: Provider */}
        {step === 'model' && (
          <div className="space-y-5">
            {providersLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              </div>
            ) : providers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 rounded-lg border border-dashed">
                <Cpu className="size-6 text-muted-foreground/40 mb-2" />
                <p className="text-xs text-muted-foreground">No providers configured</p>
                <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                  Go to Settings to add an LLM provider first.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border bg-card overflow-hidden">
                {/* Provider select */}
                <div className="px-3 py-3 space-y-1.5 border-b">
                  <Label htmlFor="providerId" className="text-sm font-medium">
                    Provider <span className="text-destructive">*</span>
                  </Label>
                  <select
                    id="providerId"
                    value={formData.providerId}
                    onChange={(e) => {
                      const pid = e.target.value;
                      const provider = providers.find((p) => p.id.toString() === pid);
                      const defaultModel =
                        provider?.providerModels?.find((m) => m.isDefault)
                        ?? provider?.providerModels?.[0]
                        ?? provider?.providerTemplate?.models?.[0];
                      setFormData({
                        ...formData,
                        providerId: pid,
                        providerModelId: defaultModel?.id?.toString() || '',
                      });
                    }}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select a provider...</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.displayName || p.providerTemplate.name}
                        {p.isPlatformManaged ? ' (Managed)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Model select */}
                <div className="px-3 py-3 space-y-1.5">
                  <Label htmlFor="providerModelId" className="text-sm font-medium">
                    Model <span className="text-destructive">*</span>
                  </Label>
                  <select
                    id="providerModelId"
                    value={formData.providerModelId}
                    onChange={(e) => setFormData({ ...formData, providerModelId: e.target.value })}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                    disabled={!formData.providerId}
                  >
                    <option value="">Select a model...</option>
                    {availableModels.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.slug})
                      </option>
                    ))}
                  </select>
                  {formData.providerModelId && (() => {
                    const model = availableModels.find((m) => m.id.toString() === formData.providerModelId);
                    return model ? (
                      <p className="text-[10px] text-muted-foreground font-mono px-0.5">
                        {model.slug}
                        {model.contextWindow ? ` · ${(model.contextWindow / 1000).toFixed(0)}k context` : ''}
                      </p>
                    ) : null;
                  })()}
                </div>
              </div>
            )}

            {/* Temperature & Max Tokens */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border bg-card px-3 py-3 space-y-1.5">
                <Label htmlFor="temperature" className="text-sm font-medium">Temperature</Label>
                <Input
                  id="temperature"
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={formData.temperature}
                  onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                  className="h-8 text-sm"
                />
                <p className="text-[10px] text-muted-foreground">0 = deterministic, 1 = creative</p>
              </div>
              <div className="rounded-lg border bg-card px-3 py-3 space-y-1.5">
                <Label htmlFor="maxTokens" className="text-sm font-medium">Max Tokens</Label>
                <Input
                  id="maxTokens"
                  type="number"
                  step="256"
                  min="1024"
                  max="200000"
                  value={formData.maxTokens}
                  onChange={(e) => setFormData({ ...formData, maxTokens: e.target.value })}
                  className="h-8 text-sm"
                />
                <p className="text-[10px] text-muted-foreground">Max response length</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Footer ────────────────────────── */}
      <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-t bg-muted/30">
        <div>
          {currentStepIndex > 0 && (
            <Button variant="ghost" size="sm" onClick={handleBack} disabled={saving} className="h-8 text-xs">
              <ChevronLeft className="size-3.5 mr-1" />
              Back
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving} className="h-8 text-xs">
            Cancel
          </Button>
          {currentStepIndex < steps.length - 1 ? (
            <Button
              size="sm"
              onClick={handleNext}
              disabled={saving || !canProceed}
              className="h-8 text-xs"
              title={!canProceed ? currentStepValidation.error : ''}
            >
              Next
              <ChevronRight className="size-3.5 ml-1" />
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={saving || !canProceed}
              className="h-8 text-xs"
              title={!canProceed ? currentStepValidation.error : ''}
            >
              {saving ? (
                <>
                  <Loader2 className="size-3.5 mr-1.5 animate-spin" />
                  Saving...
                </>
              ) : (
                agent ? 'Save Changes' : 'Create Agent'
              )}
            </Button>
          )}
        </div>
      </div>
    </>
  );

  if (variant === 'panel') {
    return (
      <div className="flex flex-col h-full">
        <SectionHeader
          icon={<Bot className="size-4 text-primary" />}
          title={agent ? 'Edit Agent' : 'Create Agent'}
          subtitle={`${STEP_META[step].label} • Step ${currentStepIndex + 1} of ${steps.length}`}
          footer={stepIndicator}
        />
        {content}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-background rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col border">

        {/* ─── Header ────────────────────────── */}
        <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center size-8 rounded-lg bg-primary/10">
                <Bot className="size-4 text-primary" />
              </div>
              <div>
                <h2 className="text-sm sm:text-base font-semibold leading-tight">
                  {agent ? 'Edit Agent' : 'Create Agent'}
                </h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {STEP_META[step].label} &middot; Step {currentStepIndex + 1} of {steps.length}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="size-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <X className="size-4" />
            </button>
          </div>

          {/* Step indicator */}
          {stepIndicator}
        </div>

        {content}
      </div>
    </div>
  );
}
